import { API_BASE_URL, AUTH_URL } from "../constants";

// Auth service base URL (for endpoints served by auth service, not n8n)
export const authFetch = async (endpoint, options = {}) => {
  const method = options.method || "GET";
  const headers = {};
  if (method !== "GET" && method !== "HEAD") {
    headers["Content-Type"] = "application/json";
  }
  const token = localStorage.getItem("hyveai_token");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const response = await fetch(`${AUTH_URL}${endpoint}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  });
  if (response.status === 401) {
    localStorage.removeItem("hyveai_token");
    window.location.reload();
    return {};
  }
  if (!response.ok) {
    const text = await response.text();
    console.error(`authFetch error ${response.status}:`, text);
    return { error: text };
  }
  try {
    return await response.json();
  } catch {
    return {};
  }
};

// Helper to convert PascalCase to camelCase
const toCamelCase = (str) => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

// Helper to normalize object keys from PascalCase to camelCase
const normalizeKeys = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => normalizeKeys(item));
  }

  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = toCamelCase(key);
      acc[camelKey] = normalizeKeys(obj[key]);
      return acc;
    }, {});
  }

  return obj;
};

// Helper to process API response (handles nested structure with cursor AND normalizes keys)
const processApiResponse = (data) => {
  // Handle array responses
  if (Array.isArray(data)) {
    // Check if it's a cursor-based response
    if (data.length > 0 && data[0].data) {
      // Return data directly without slicing (cursor metadata already handled by backend)
      return normalizeKeys(data[0].data);
    }
    // Direct array response - normalize keys
    return normalizeKeys(data);
  }

  // Handle single object response
  if (data && typeof data === "object") {
    return normalizeKeys(data);
  }

  return [];
};

// Helper to map field names (handles both PascalCase and lowercase)
const mapFields = (item, fieldMap) => {
  const mapped = {};
  for (const [key, variants] of Object.entries(fieldMap)) {
    // Try each variant until we find a value
    for (const variant of variants) {
      if (item[variant] !== undefined) {
        mapped[key] = item[variant];
        break;
      }
    }
  }
  return mapped;
};

// Auth token helper
const getAuthToken = () => localStorage.getItem("hyveai_token");

// Generic fetch function with auth header injection
const apiFetch = async (endpoint, options = {}) => {
  const method = options.method || "GET";
  const headers = {};

  if (method !== "GET" && method !== "HEAD") {
    headers["Content-Type"] = "application/json";
  }

  // Inject auth token if available
  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  // On 401, clear token and reload to trigger login
  if (response.status === 401) {
    localStorage.removeItem("hyveai_token");
    window.location.reload();
    return {};
  }

  return response.json();
};

// ==================== CLIENTS ====================
export const clientsApi = {
  getAll: async () => {
    const data = await apiFetch("/get-client");
    return processApiResponse(data);
  },

  create: async (clientData) => {
    return apiFetch("/set-client", {
      method: "POST",
      body: JSON.stringify(clientData),
    });
  },

  update: async (id, clientData) => {
    return apiFetch("/set-client", {
      method: "POST",
      body: JSON.stringify({ ...clientData, id }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-client", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// ==================== USERS ====================
export const usersApi = {
  getAll: async () => {
    const data = await apiFetch("/get-user");
    return processApiResponse(data);
  },

  create: async (userData) => {
    return apiFetch("/set-user", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  },

  update: async (id, userData) => {
    return apiFetch("/set-user", {
      method: "POST",
      body: JSON.stringify({ ...userData, id }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-user", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },

  associateClient: async (userId, clientId, role) => {
    return apiFetch("/set-user-client", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        client_id: clientId,
        role,
      }),
    });
  },
  getAllAssociations: async () => {
    const data = await apiFetch("/get-user-client");
    return processApiResponse(data);
  },
};
// ==================== USERSCLIENT ====================
export const userClientApi = {
  getAll: async () => {
    const data = await apiFetch("/get-user-client");
    return processApiResponse(data);
  },
};
// ==================== DATAMAPS ====================
export const datamapsApi = {
  getAll: async () => {
    const data = await apiFetch("/get-datamap");
    const raw = processApiResponse(data);

    // Map fields (DataMaps use PascalCase)
    return raw.map((item) =>
      mapFields(item, {
        id: ["ID", "id"],
        customerid: ["CustomerID", "customerid"],
        frequency: ["Frequency", "frequency"],
        active: ["Active", "active"],
        prompt: ["Prompt", "prompt"],
        name: ["Name", "name"],
        deliverymethod: ["DeliveryMethod", "deliverymethod"],
        destination: ["Destination", "destination"],
        clientid: ["client_id", "clientid"],
      }),
    );
  },

  create: async (datamapData) => {
    return apiFetch("/set-datamap", {
      method: "POST",
      body: JSON.stringify(datamapData),
    });
  },

  update: async (id, datamapData) => {
    return apiFetch("/set-datamap", {
      method: "POST",
      body: JSON.stringify({ ...datamapData, id }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-datamap", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// ==================== INDICATORS ====================
export const indicatorsApi = {
  getAll: async () => {
    const data = await apiFetch("/get-indicator");
    const raw = processApiResponse(data);

    // Map fields
    return raw.map((item) =>
      mapFields(item, {
        id: ["ID", "id"],
        datamapid: ["DataMapID", "datamapid"],
        customerid: ["CustomerID", "customerid"],
        dataindicator: ["Data_Indicator", "data_indicator"],
        source: ["Source", "source"],
        frequency: ["Frequency", "frequency"],
        active: ["Active", "active"],
        token: ["Token", "token"],
        clientid: ["client_id", "clientid"],
        body: ["Body", "body"],
        token_name: ["Token_Name", "token_name"],
        description: ["Description", "description"],
        category: ["Category", "category"],
        subcategory: [
          "Sub_Category",
          "sub_category",
          "Subcategory",
          "subcategory",
        ],
        private: ["Private", "private"],
      }),
    );
  },

  getCategories: async () => {
    const data = await apiFetch("/get-indicator-category");
    const raw = processApiResponse(data);

    // Map fields for categories and subcategories
    return raw.map((item) =>
      mapFields(item, {
        category: ["Category", "category"],
        subcategory: [
          "Sub_Category",
          "sub_category",
          "Subcategory",
          "subcategory",
        ],
      }),
    );
  },

  create: async (indicatorData) => {
    return apiFetch("/set-indicator", {
      method: "POST",
      body: JSON.stringify(indicatorData),
    });
  },

  update: async (id, indicatorData) => {
    return apiFetch("/set-indicator", {
      method: "POST",
      body: JSON.stringify({ ...indicatorData, id }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-indicator", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// ==================== RESULTS ====================
export const resultsApi = {
  getAll: async () => {
    const data = await apiFetch("/get-result");
    const raw = processApiResponse(data);

    // Map fields
    return raw.map((item) =>
      mapFields(item, {
        id: ["ID", "id"],
        customerid: ["CustomerID", "customerid"],
        dataindicatorid: ["DataIndicatorID", "dataindicatorid"],
        value: ["Value", "value"],
        description: ["Description", "description"],
        loaddatetime: ["LoadDateTime", "loaddatetime"],
        clientid: ["client_id", "clientid"],
      }),
    );
  },

  create: async (resultData) => {
    return apiFetch("/set-result", {
      method: "POST",
      body: JSON.stringify(resultData),
    });
  },

  update: async (id, resultData) => {
    return apiFetch("/set-result", {
      method: "POST",
      body: JSON.stringify({ ...resultData, id }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-result", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// ==================== DATAMAP INDICATORS (ASSOCIATION) ====================
export const datamapIndicatorsApi = {
  getByDatamap: async (datamapId) => {
    const data = await apiFetch(
      `/get-datamap-dataindicator?datamapId=${datamapId}`,
    );
    const raw = processApiResponse(data);
    // Map fields
    return raw.map((item) =>
      mapFields(item, {
        id: ["ID", "id"],
        datamapid: ["DataMapID", "datamapid", "datamap_id"], // Added snake_case
        dataindicatorid: ["DataIndicator_id", "dataindicator_id"], // Added snake_case
        dataindicator: ["Data_Indicator", "data_indicator", "dataindicator"], // Added snake_case
      }),
    );
  },

  create: async (associationData) => {
    return apiFetch("/set-datamap-dataindicator", {
      method: "POST",
      body: JSON.stringify({
        dataMapID: associationData.dataMapID || 0,
        dataIndicatorID: associationData.dataIndicatorID || 0,
      }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-datamap-dataindicator", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// ==================== PODCAST APIS ====================

// Podcasts API
export const podcastsApi = {
  getAll: async () => {
    const data = await apiFetch("/get-podcasts");
    return processApiResponse(data);
  },

  create: async (podcastData) => {
    return apiFetch("/set-podcast", {
      method: "POST",
      body: JSON.stringify({
        id: 0,
        title: podcastData.title || "",
        intro: podcastData.intro || "",
        outro: podcastData.outro || "",
      }),
    });
  },

  update: async (id, podcastData) => {
    return apiFetch("/set-podcast", {
      method: "POST",
      body: JSON.stringify({
        id: id,
        title: podcastData.title || "",
        intro: podcastData.intro || "",
        outro: podcastData.outro || "",
      }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-podcast", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// Episodes API
export const episodesApi = {
  getAll: async () => {
    const data = await apiFetch("/get-episodes");
    return processApiResponse(data);
  },

  create: async (episodeData) => {
    return apiFetch("/set-episode", {
      method: "POST",
      body: JSON.stringify({
        id: 0,
        status: episodeData.status || "Draft",
        title: episodeData.title || "",
        topic: episodeData.topic || "",
        podCastID: episodeData.podCastID || 0,
      }),
    });
  },

  update: async (id, episodeData) => {
    return apiFetch("/set-episode", {
      method: "POST",
      body: JSON.stringify({
        id: id,
        status: episodeData.status || "Draft",
        title: episodeData.title || "",
        topic: episodeData.topic || "",
        podCastID: episodeData.podCastID || 0,
      }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-episode", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },

  copy: async (srcEpisodeGuideId) => {
    return apiFetch("/copy-episode", {
      method: "POST",
      body: JSON.stringify({ srcEpisodeGuideId }),
    });
  },
};

// Hosts API
export const hostsApi = {
  getAll: async () => {
    const data = await apiFetch("/get-hosts");
    return processApiResponse(data);
  },

  create: async (hostData) => {
    return apiFetch("/set-host", {
      method: "POST",
      body: JSON.stringify({
        id: 0,
        status: hostData.status || "Active",
        title: hostData.title || "",
        topic: hostData.topic || "",
        podCastID: hostData.podCastID || 0,
      }),
    });
  },

  update: async (id, hostData) => {
    return apiFetch("/set-host", {
      method: "POST",
      body: JSON.stringify({
        id: id,
        status: hostData.status || "Active",
        title: hostData.title || "",
        topic: hostData.topic || "",
        podCastID: hostData.podCastID || 0,
      }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-host", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// Prompts API
export const promptsApi = {
  getAll: async () => {
    const data = await apiFetch("/get-prompts");
    return processApiResponse(data);
  },

  create: async (promptData) => {
    return apiFetch("/set-prompt", {
      method: "POST",
      body: JSON.stringify({
        id: 0,
        name: promptData.name || "",
        promptText: promptData.promptText || "",
        subPromptText: promptData.subPromptText || "",
        description: promptData.description || "",
        platform: promptData.platform || "",
      }),
    });
  },

  update: async (id, promptData) => {
    return apiFetch("/set-prompt", {
      method: "POST",
      body: JSON.stringify({
        id: id,
        name: promptData.name || "",
        promptText: promptData.promptText || "",
        subPromptText: promptData.subPromptText || "",
        description: promptData.description || "",
        platform: promptData.platform || "",
      }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-prompt", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// Styles API
export const stylesApi = {
  getAll: async () => {
    const data = await apiFetch("/get-styles");
    return processApiResponse(data);
  },

  create: async (styleData) => {
    return apiFetch("/set-style", {
      method: "POST",
      body: JSON.stringify({
        id: 0,
        status: styleData.status || "Active",
        title: styleData.title || "",
        topic: styleData.topic || "",
        StyleID: styleData.StyleID || 0,
      }),
    });
  },

  update: async (id, styleData) => {
    return apiFetch("/set-style", {
      method: "POST",
      body: JSON.stringify({
        id: id,
        status: styleData.status || "Active",
        title: styleData.title || "",
        topic: styleData.topic || "",
        StyleID: styleData.StyleID || 0,
      }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-style", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// Episode Hosts API (association table)
export const episodeHostsApi = {
  getByEpisode: async (episodeId) => {
    const data = await apiFetch(`/get-episode-hosts?episodeId=${episodeId}`);
    return processApiResponse(data);
  },

  create: async (episodeHostData) => {
    return apiFetch("/set-episode-host", {
      method: "POST",
      body: JSON.stringify({
        id: 0,
        episodeGuideID: episodeHostData.episodeGuideID || 0,
        hostID: episodeHostData.hostID || 0,
        hostName: episodeHostData.hostName || "",
        hostType: episodeHostData.hostType || "Host",
        gender: episodeHostData.gender || "",
        style: episodeHostData.style || "",
      }),
    });
  },

  update: async (id, episodeHostData) => {
    return apiFetch("/set-episode-host", {
      method: "POST",
      body: JSON.stringify({
        id: id,
        episodeGuideID: episodeHostData.episodeGuideID || 0,
        hostID: episodeHostData.hostID || 0,
        hostName: episodeHostData.hostName || "",
        hostType: episodeHostData.hostType || "Host",
        gender: episodeHostData.gender || "",
        style: episodeHostData.style || "",
      }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-episode-host", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// Episode Prompts API (association table)
export const episodePromptsApi = {
  getByEpisode: async (episodeId) => {
    const data = await apiFetch(`/get-episode-prompts?episodeId=${episodeId}`);
    return processApiResponse(data);
  },

  create: async (episodePromptData) => {
    return apiFetch("/set-episode-prompt", {
      method: "POST",
      body: JSON.stringify({
        episodeID: episodePromptData.episodeID || 0,
        promptID: episodePromptData.promptID || 0,
      }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-episode-prompt", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// ==================== END PODCAST APIS ====================

// ==================== PRODUCTS (AI Product Catalog) ====================
export const productsApi = {
  getAll: async () => {
    const data = await apiFetch("/get-product");
    return processApiResponse(data);
  },

  create: async (productData) => {
    return apiFetch("/set-product", {
      method: "POST",
      body: JSON.stringify({
        id: 0,
        product_code: productData.product_code || "",
        product_name: productData.product_name || "",
        description: productData.description || "",
        product_type: productData.product_type || "",
        includes_rag: productData.includes_rag || false,
        includes_slm: productData.includes_slm || false,
        includes_private_llm: productData.includes_private_llm || false,
        base_price: productData.base_price || 0,
        billing_cycle: productData.billing_cycle || "monthly",
        is_active: productData.is_active ?? true,
        included_services: productData.included_services || [],
      }),
    });
  },

  update: async (id, productData) => {
    return apiFetch("/set-product", {
      method: "POST",
      body: JSON.stringify({
        ...productData,
        id,
      }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-product", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// ==================== ORDERS ====================
export const ordersApi = {
  getAll: async () => {
    const data = await apiFetch("/get-order");
    return processApiResponse(data);
  },

  getByFacility: async (facilityId) => {
    const data = await apiFetch(`/get-order?facility_id=${facilityId}`);
    return processApiResponse(data);
  },

  create: async (orderData) => {
    return apiFetch("/set-order", {
      method: "POST",
      body: JSON.stringify({
        id: 0,
        order_number: orderData.order_number || "",
        facility_id: orderData.facility_id || "",
        product_code: orderData.product_code || "",
        status: orderData.status || "pending",
        monthly_price: orderData.monthly_price || 0,
        notes: orderData.notes || "",
      }),
    });
  },

  update: async (id, orderData) => {
    return apiFetch("/set-order", {
      method: "POST",
      body: JSON.stringify({ ...orderData, id }),
    });
  },

  delete: async (id) => {
    return apiFetch("/delete-order", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },

  provision: async (id) => {
    return apiFetch("/provision-order", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },

  activate: async (id) => {
    return apiFetch("/activate-order", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },

  suspend: async (id) => {
    return apiFetch("/suspend-order", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },

  cancel: async (id) => {
    return apiFetch("/cancel-order", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

// ==================== FACILITY SERVICES ====================
export const facilityServicesApi = {
  getAll: async () => {
    const data = await apiFetch("/get-facility-service");
    return processApiResponse(data);
  },

  getByFacility: async (facilityId) => {
    const data = await apiFetch(
      `/get-facility-service?facility_id=${facilityId}`,
    );
    return processApiResponse(data);
  },

  update: async (id, serviceData) => {
    return apiFetch("/set-facility-service", {
      method: "POST",
      body: JSON.stringify({ ...serviceData, id }),
    });
  },

  updateConfig: async (id, config) => {
    return apiFetch("/set-facility-service-config", {
      method: "POST",
      body: JSON.stringify({ id, config }),
    });
  },
};

// ==================== USER SERVICE ACCESS ====================
// Grant and revoke go through the auth service, not the n8n CRUD webhooks.
// The n8n revoke was `DELETE FROM user_service_access WHERE id = …`, behind a
// confirm that said only "Revoke this user's service access?" — the same
// wording as the reversible revoke on the Facility Users screen, one screen
// away, so the two were indistinguishable to whoever clicked. Access changes
// now live behind this service's auth, and revoking is undoable.
export const userServiceAccessApi = {
  getAll: async () => {
    // Includes revoked rows. The n8n listing did not, so a soft revoke would
    // have hidden the row just as completely as deleting it did.
    return authFetch("/admin/service-access");
  },

  getByUser: async (userId) => {
    const data = await apiFetch(`/get-user-service-access?user_id=${userId}`);
    return processApiResponse(data);
  },

  getByFacility: async (facilityId) => {
    const data = await apiFetch(
      `/get-user-service-access?facility_id=${facilityId}`,
    );
    return processApiResponse(data);
  },

  // Also the restore path: the endpoint's grant branch is
  // INSERT … ON CONFLICT DO UPDATE SET is_active = true, revoked_at = NULL,
  // so re-granting the same triple un-revokes the existing row rather than
  // creating a second one.
  grant: async (accessData) => {
    return authFetch("/facility-users/set-service-access", {
      method: "POST",
      body: JSON.stringify({
        user_id: accessData.user_id,
        facility_id: accessData.facility_id,
        service_type: accessData.service_type,
        access_role: accessData.access_role || "user",
      }),
    });
  },

  // Soft: is_active = false, revoked_at = NOW(). The row keeps its history
  // and `grant` brings it back. Takes the triple rather than the row id
  // because that is what the endpoint keys on — and because passing an id to
  // something called "revoke" is how the delete slipped in unnoticed.
  revoke: async ({ user_id, facility_id, service_type }) => {
    return authFetch("/facility-users/set-service-access", {
      method: "POST",
      body: JSON.stringify({
        user_id,
        facility_id,
        service_type,
        revoke: true,
      }),
    });
  },
};

// ==================== PLATFORM USERS (via auth service) ====================
export const platformUsersApi = {
  getAll: async () => {
    const data = await authFetch("/facility-users");
    return data.users || [];
  },
};

// ==================== SFTP MANAGEMENT (via auth service) ====================
export const sftpApi = {
  getStatus: async () => {
    const data = await authFetch("/sftp/status");
    return Array.isArray(data) ? data : [];
  },

  provision: async (facilityId, password, subdirs = []) => {
    return authFetch("/sftp/provision", {
      method: "POST",
      body: JSON.stringify({
        facility_id: facilityId,
        password,
        subdirs: subdirs.length > 0 ? subdirs : undefined,
      }),
    });
  },
};

// ==================== BILLING (via auth service) ====================
export const billingApi = {
  getProducts: async () => {
    const data = await authFetch("/billing/products");
    return Array.isArray(data) ? data : [];
  },

  getSubscription: async (facilityId) => {
    return authFetch(`/billing/subscription?facility_id=${facilityId}`);
  },

  getInvoices: async (facilityId) => {
    const data = await authFetch(`/billing/invoices?facility_id=${facilityId}`);
    return Array.isArray(data) ? data : [];
  },

  getUsage: async (facilityId, period = "30d") => {
    return authFetch(
      `/billing/usage?facility_id=${facilityId}&period=${period}`,
    );
  },

  createCheckout: async (facilityId, productCode, billingCycle = "monthly") => {
    return authFetch("/billing/create-checkout", {
      method: "POST",
      body: JSON.stringify({
        facility_id: facilityId,
        product_code: productCode,
        billing_cycle: billingCycle,
      }),
    });
  },

  openPortal: async (facilityId) => {
    return authFetch("/billing/portal", {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId }),
    });
  },

  cancel: async (facilityId) => {
    return authFetch("/billing/cancel", {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId }),
    });
  },
};

// ==================== CLIENT PORTAL (via auth service) ====================
export const portalApi = {
  getMyFacilities: async () => {
    const data = await authFetch("/my/facilities");
    return Array.isArray(data) ? data : [];
  },

  getConversations: async (facilityId, page = 1, search = "") => {
    const params = new URLSearchParams({
      facility_id: facilityId,
      page: String(page),
      per_page: "25",
    });
    if (search) params.set("search", search);
    return authFetch(`/my/conversations?${params}`);
  },

  getConversationDetail: async (sessionId) => {
    return authFetch(`/my/conversations/${sessionId}`);
  },

  getMyUsage: async (facilityId, period = "30d") => {
    return authFetch(`/my/usage?facility_id=${facilityId}&period=${period}`);
  },

  getMyDocuments: async (facilityId) => {
    const data = await authFetch(`/my/documents?facility_id=${facilityId}`);
    return Array.isArray(data) ? data : [];
  },

  getMyProfile: async () => {
    return authFetch("/my/profile");
  },

  updateMyProfile: async (profileData) => {
    return authFetch("/my/profile", {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
  },
};

// ==================== CLIENTS (via auth service, CORS-safe) ====================
export const clientsAuthApi = {
  getAll: async () => {
    const data = await authFetch("/clients");
    return Array.isArray(data) ? data : [];
  },

  update: async (id, clientData) => {
    return authFetch("/clients", {
      method: "POST",
      body: JSON.stringify({ id, ...clientData }),
    });
  },

  create: async (clientData) => {
    return authFetch("/clients", {
      method: "POST",
      body: JSON.stringify(clientData),
    });
  },

  getFacilities: async (clientId) => {
    const data = await authFetch(`/clients/facilities?client_id=${clientId}`);
    return Array.isArray(data) ? data : [];
  },

  getUsers: async (clientId) => {
    const data = await authFetch(`/clients/users?client_id=${clientId}`);
    return data.users || [];
  },

  linkFacility: async (facilityId, clientId) => {
    return authFetch("/clients/link-facility", {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId, client_id: clientId }),
    });
  },
};

// ==================== FACILITY SERVICES (via auth service, CORS-safe) ====================
export const facilityServicesAuthApi = {
  getByFacility: async (facilityId) => {
    const data = await authFetch(
      `/facility-services?facility_id=${facilityId}`,
    );
    return Array.isArray(data) ? data : [];
  },

  set: async (serviceData) => {
    return authFetch("/facility-services", {
      method: "POST",
      body: JSON.stringify(serviceData),
    });
  },
};

// ==================== FACILITIES (via auth service) ====================
export const facilitiesApi = {
  getAll: async () => {
    const data = await authFetch("/facilities");
    return Array.isArray(data) ? data : [];
  },

  getById: async (facilityId) => {
    const data = await authFetch(`/facilities?facility_id=${facilityId}`);
    return Array.isArray(data) ? data : [];
  },

  create: async (facilityData) => {
    return authFetch("/facilities", {
      method: "POST",
      body: JSON.stringify({
        id: 0,
        facility_id: facilityData.facility_id || facilityData.facilityId || "",
        facility_name:
          facilityData.facility_name || facilityData.facilityName || "",
        facility_type:
          facilityData.facility_type || facilityData.facilityType || "business",
        contact_email:
          facilityData.contact_email || facilityData.contactEmail || "",
        contact_phone:
          facilityData.contact_phone || facilityData.contactPhone || "",
        address: facilityData.address || "",
        timezone: facilityData.timezone || "America/New_York",
        status: facilityData.status || "active",
        vertical_id:
          facilityData.vertical_id || facilityData.verticalId || "retail",
        base_model_id:
          facilityData.base_model_id ||
          facilityData.baseModelId ||
          "mistral-7b",
        model_tier:
          facilityData.model_tier || facilityData.modelTier || "premium",
        training_enabled:
          facilityData.training_enabled ?? facilityData.trainingEnabled ?? true,
      }),
    });
  },

  update: async (id, facilityData) => {
    return authFetch("/facilities", {
      method: "POST",
      body: JSON.stringify({
        id,
        facility_id: facilityData.facility_id || facilityData.facilityId || "",
        facility_name:
          facilityData.facility_name || facilityData.facilityName || "",
        facility_type:
          facilityData.facility_type || facilityData.facilityType || undefined,
        contact_email:
          facilityData.contact_email || facilityData.contactEmail || undefined,
        contact_phone:
          facilityData.contact_phone || facilityData.contactPhone || undefined,
        address: facilityData.address || undefined,
        timezone: facilityData.timezone || undefined,
        status: facilityData.status || undefined,
        vertical_id:
          facilityData.vertical_id || facilityData.verticalId || undefined,
        base_model_id:
          facilityData.base_model_id || facilityData.baseModelId || undefined,
        model_tier:
          facilityData.model_tier || facilityData.modelTier || undefined,
        training_enabled:
          facilityData.training_enabled ??
          facilityData.trainingEnabled ??
          undefined,
        webhook_url:
          facilityData.webhook_url || facilityData.webhookUrl || undefined,
        webhook_enabled:
          facilityData.webhook_enabled ??
          facilityData.webhookEnabled ??
          undefined,
        source_registry_namespace:
          facilityData.source_registry_namespace ||
          facilityData.sourceRegistryNamespace ||
          undefined,
        display_name: facilityData.display_name || undefined,
        agent_name: facilityData.agent_name || undefined,
        brand_color: facilityData.brand_color || undefined,
        static_base_url: facilityData.static_base_url || undefined,
        allowed_origins: facilityData.allowed_origins || undefined,
        system_prompt: facilityData.system_prompt || undefined,
        sheets_log_tab: facilityData.sheets_log_tab || undefined,
        enrichment_rules: facilityData.enrichment_rules ?? undefined,
        // null = reset to vertical defaults; undefined (key absent) = leave unchanged
        ...("enabled_tabs" in facilityData
          ? { enabled_tabs: facilityData.enabled_tabs }
          : {}),
      }),
    });
  },

  delete: async (id) => {
    return authFetch("/facilities/delete", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};

export const verticalsApi = {
  getAll: async () => {
    const data = await authFetch("/verticals");
    return Array.isArray(data) ? data : [];
  },
};
